import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  TextField,
} from "@mui/material";
import { ArrowBack, CheckCircle, Cancel, Print } from "@mui/icons-material";
import itemIssueReturnService from "../../services/itemIssueReturnService";
import { useSnackbar } from "notistack";
import { useAuth } from "../../contexts/AuthContext";

export const ItemIssueReturnDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { hasAnyRole } = useAuth();

  const [returnRecord, setReturnRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionDialog, setActionDialog] = useState({
    open: false,
    type: "",
    notes: "",
  });
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchReturnDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchReturnDetails = async () => {
    try {
      setLoading(true);
      const response = await itemIssueReturnService.getItemIssueReturn(id);
      setReturnRecord(response.data.data);
    } catch (error) {
      enqueueSnackbar("Error fetching return details", { variant: "error" });
      navigate("/item-issue-returns");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = () => {
    setActionDialog({ open: true, type: "approve", notes: "" });
  };

  const handleReject = () => {
    setActionDialog({ open: true, type: "reject", notes: "" });
  };

  const handleConfirmAction = async () => {
    setActionLoading(true);
    try {
      if (actionDialog.type === "approve") {
        await itemIssueReturnService.approveItemIssueReturn(id, {
          notes: actionDialog.notes,
        });
        enqueueSnackbar("Return approved successfully", { variant: "success" });
      } else if (actionDialog.type === "reject") {
        await itemIssueReturnService.rejectItemIssueReturn(id, {
          rejectionReason: actionDialog.notes
        });
        enqueueSnackbar("Return rejected successfully", { variant: "success" });
      }

      fetchReturnDetails();
      setActionDialog({ open: false, type: "", notes: "" });
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || "Error processing return",
        { variant: "error" }
      );
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "warning",
      approved: "info",
      rejected: "error",
      completed: "success",
      cancelled: "default",
    };
    return colors[status] || "default";
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!returnRecord) {
    return <Alert severity="error">Return record not found</Alert>;
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/item-issue-returns")}
        >
          Back to Returns
        </Button>
        <Typography variant="h4" fontWeight="bold">
          Return #{returnRecord.returnNumber}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            startIcon={<Print />}
            onClick={handlePrint}
            variant="outlined"
          >
            Print
          </Button>

          {returnRecord.status === "pending" &&
            hasAnyRole(["admin", "inventory_manager"]) && (
              <>
                <Button
                  startIcon={<CheckCircle />}
                  variant="contained"
                  color="success"
                  onClick={handleApprove}
                >
                  Approve
                </Button>
                <Button
                  startIcon={<Cancel />}
                  variant="outlined"
                  color="error"
                  onClick={handleReject}
                >
                  Reject
                </Button>
              </>
            )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Return Details */}
        <Grid item xs={12} md={8}>
          {/* Basic Information */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Return Information
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Return Number
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {returnRecord.returnNumber}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={returnRecord.status.toUpperCase()}
                    color={getStatusColor(returnRecord.status)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Return Date
                  </Typography>
                  <Typography variant="body1">
                    {new Date(
                      returnRecord.returnDate || returnRecord.createdAt
                    ).toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Department
                  </Typography>
                  <Typography variant="body1">
                    {returnRecord.department?.departmentName || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Original Issue
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {returnRecord.itemIssue?.issueNumber || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Reason
                  </Typography>
                  <Typography variant="body1">{returnRecord.reason}</Typography>
                </Grid>
                {returnRecord.notes && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Notes
                    </Typography>
                    <Typography variant="body1">
                      {returnRecord.notes}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Returned Items */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Returned Items
              </Typography>

              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell>Batch</TableCell>
                      <TableCell align="center">Quantity</TableCell>
                      <TableCell align="right">Cost Price</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell align="center">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {returnRecord.items?.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {item.item?.name || "N/A"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {item.item?.brand}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {item.batchStock?.batchNumber || "N/A"}
                          </Typography>
                          {item.batchStock?.rack && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              Rack:{" "}
                              {item.batchStock.rack.name ||
                                item.batchStock.rack}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="medium">
                            {item.quantity}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            ₹{item.costPrice?.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium">
                            ₹{(item.quantity * item.costPrice).toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {item.reason}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {item.restockable && (
                            <Chip
                              label="Restockable"
                              color="success"
                              size="small"
                            />
                          )}
                          {item.disposalRequired && (
                            <Chip label="Disposal" color="error" size="small" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Summary */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Return Summary
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2">Subtotal:</Typography>
                  <Typography variant="body2">
                    ₹{returnRecord.subtotal?.toFixed(2)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2">Tax:</Typography>
                  <Typography variant="body2">
                    ₹{returnRecord.taxAmount?.toFixed(2)}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="h6" fontWeight="bold">
                    Total:
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color="primary.main"
                  >
                    ₹{returnRecord.totalAmount?.toFixed(2)}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {returnRecord.createdBy && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Created By
                  </Typography>
                  <Typography variant="body1">
                    {returnRecord.createdBy.name ||
                      returnRecord.createdBy.email}
                  </Typography>
                </Box>
              )}

              {returnRecord.approvedBy && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Approved By
                  </Typography>
                  <Typography variant="body1">
                    {returnRecord.approvedBy.name ||
                      returnRecord.approvedBy.email}
                  </Typography>
                </Box>
              )}

              {returnRecord.rejectedBy && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Rejected By
                  </Typography>
                  <Typography variant="body1">
                    {returnRecord.rejectedBy.name ||
                      returnRecord.rejectedBy.email}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Dialog */}
      <Dialog
        open={actionDialog.open}
        onClose={() => setActionDialog({ open: false, type: "", notes: "" })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {actionDialog.type === "approve" ? "Approve Return" : "Reject Return"}
        </DialogTitle>
        <DialogContent>
          <Alert
            severity={actionDialog.type === "approve" ? "info" : "warning"}
            sx={{ mb: 2 }}
          >
            {actionDialog.type === "approve"
              ? "Approving this return will restore items to inventory."
              : "Rejecting this return will cancel the return request."}
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes (Optional)"
            value={actionDialog.notes}
            onChange={(e) =>
              setActionDialog({ ...actionDialog, notes: e.target.value })
            }
            placeholder="Add any notes about this action"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setActionDialog({ open: false, type: "", notes: "" })
            }
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color={actionDialog.type === "approve" ? "success" : "error"}
            onClick={handleConfirmAction}
            disabled={actionLoading}
          >
            {actionLoading ? (
              <CircularProgress size={24} />
            ) : actionDialog.type === "approve" ? (
              "Approve"
            ) : (
              "Reject"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
